import { SvgPlacesStorageModel } from "../models/SvgPlacesStorageModel";
import { StorageService } from "./StorageService";
import { helpers } from "../helpers";
import api from "../api";

/**
 * Класс для работы с хранением и получением цен и свг сектора в сторидже
 * лимит хранения в сторидже получаем из параметра storageLimit
 * также имеет свою модель для работы с данными SvgPlacesStorageModel
 *
 * @example Пример как использовать:
 * this.SvgPlacesManager = new SvgPlacesManager({ storage, storageLimit }) - инициализируем в js
 * this.SvgPlacesManager.getSvgPlaces().then().catch() - вызываем метод получения свг и цен
 * this.SvgPlacesManager.saveToStorage() - вызываем метод для сохранения в сторидж
 */
export class SvgPlacesManager {
  /**
   * @param params { Object } - { storage, storageLimit, useStorage }
   */
  constructor(params = {}) {
    this.model = new SvgPlacesStorageModel();
  
    this.useStorage = params.useStorage !== false;
    this.storage = this.useStorage ? new StorageService() : null;
    /* Переводим в миллисекунды (по умолчанию 3 минуты) */
    this.storageLimit = (params.storageLimit || 3) * 60 * 1000;
  }
  
  /**
   * @param limit {Number}
   * @return {Number, Boolean}
   */
  setStorageLimit(limit) {
    if (!helpers.isNumber(limit)) return false;
    return this.storageLimit = limit
  }
  
  /**
   * Возвращает план и места сектора данного мероприятия
   * если данные уже есть в сторадже то попытается вернуть их
   * @param eventId {Number}
   * @param viewId {Number}
   * @param unitId {Number}
   * @return {Promise<import("../models/SvgPlacesStorageModel").SvgPlacesData>}
   */
  getSvgPlaces(eventId, viewId, unitId) {
    return new Promise((resolve) => {
      try {
        if (!this.useStorage) return this.updateSvgPlacesHandler(eventId, viewId).then(res => resolve(res));
        if (!this.storage) throw new Error("Не удалось получить функцию сториджа!");
        if (!unitId) throw new Error("Не удалось получить id юнита!");

        this.storage.getItem(`${unitId}_${viewId}`).then(res => {
          /* Не нашли в сторидже значит загрузим всё с сервера */
          if (!res) return this.updateSvgPlacesHandler(eventId, viewId).then(res => resolve(res));

          /* Загружаем полученные данные в модель проверив по мероприятию */
          this.model.setAndValidateDataByEventId(eventId, res);
          this.checkPlacesHashVersion(eventId, viewId, unitId);

          return resolve(this.model.getSvgPlacesData());
        });
      } catch (err) {
        console.log(err);
      
        this.updateSvgPlacesHandler(eventId, viewId).then(res => resolve(res));
      }
    });
  }
  
  checkPlacesHashVersion(eventId, viewId, unitId) {
    api.event.getPlacesHash({ eventId, viewId }).then(res => {
      if (!(res && res.version)) return false;
      if (this.model.version !== res.version) {
        this.storage.removeItem(`${unitId}_${viewId}`);
        return alert("Необходимо обновить сектор!");
      }
    })
  }

  /** @return {Promise<{ zones: import("../api/rest/event").Zone[], prices: import("../api/rest/event").Price[] }>} */
  getPrices(eventId, viewId) {
    return new Promise((resolve) => {
      if (!this.useStorage) return this.updatePricesHandler(eventId, viewId).then(res => resolve(res));
      if (!this.storage) throw new Error("Не удалось получить функцию сториджа!");
      if (!this.model.prices.length > 0) return this.updatePricesHandler(eventId, viewId).then(res => resolve(res));
      if (!this.model.time || this.isStorageLimitExpired()) return this.updatePricesHandler(eventId, viewId).then(res => resolve(res));

      return resolve({ prices: this.model.prices, zones: this.model.zones })
    });
  }
  
  isStorageLimitExpired() {
    return (+new Date() - this.model.time > this.storageLimit);
  }
  
  /**
   * Обновляет прайсы
   * @param eventId {Number}
   * @param viewId {Number}
   * @param clearCache {Boolean}
   * @return {Promise<{ zones: import("../api/rest/event").Zone[], prices: import("../api/rest/event").Price[] }>}
   */
  updatePricesHandler(eventId, viewId, clearCache = false) {
    return new Promise((resolve) => {
      api.event.getActualPlaces({ eventId, viewId, clearCache }).then(res => {
        if (!res.places) throw new Error("Не удалось загрузить места!");
        if (!this.model.zones.length > 0) this.model.zones = res.zones;
        this.model.setPricesByActualPlaces(res.places);
        return resolve({ prices: this.model.prices, zones: this.model.zones });
      });
    });
  }
  
  /**
   * Обновляет полностью модель и вернет промис
   * @param eventId {Number}
   * @param viewId {Number}
   * @return {Promise<import("../models/SvgPlacesStorageModel").SvgPlacesData>}
   */
  updateSvgPlacesHandler(eventId, viewId) {
    return new Promise((resolve) => {
      api.event.getSvgPlaces({eventId, viewId}).then(res => {
        this.model.insertData({
          ...res,
          eventId: eventId,
        });

        resolve(this.model.getSvgPlacesData())
      });
    });
  }
  
  /**
   * Сохраняет модель в сторидж по ключу (например: 63_1)
   * @param {Number} unitId
   * @param {Number} viewId
   */
  saveToStorage(unitId, viewId) {
    if (!this.useStorage) return false;
    try {
      if (!Number.isInteger(unitId)) throw new Error("Не удалось получить корректный unitId!");
      if (!Number.isInteger(viewId)) throw new Error("Не удалось получить корректный viewId!");
      if (!this.model.isValid()) throw new Error("Не удалось валидировать данные для сохранения в сторидж!");
      
      this.storage.setItem(`${unitId}_${viewId}`, this.model.getToStoreData());
    } catch (err) {
      console.log(err);
      
      this.storage.removeItem(`${unitId}_${viewId}`);
    }
  }
}
